"use client";

import { type ArticleFromQueryArray, type ContentHubSection } from "@lib/types";
import { stegaClean } from "@sanity/client/stega";
import { useEffect, useMemo, useRef, useState } from "react";

import { ArticleCard } from "@/components/article";
import { Button } from "@/components/ui/button";
import { Pagination } from "@/components/ui/pagination";
import { useHashAction } from "@/hooks/useHashAction";
import { useHashPagination } from "@/hooks/useHashPagination";
import { cn } from "@/lib/ui/cn";

type SectionData = {
  section: ContentHubSection;
  articles: ArticleFromQueryArray[];
};

interface ContentHubTabsProps {
  sections: SectionData[];
}

const ARTICLES_PER_PAGE = 12;

export function ContentHubTabs({ sections }: ContentHubTabsProps) {
  const [activeTab, setActiveTab] = useState(() => sections[0]?.section._key || "");
  const [currentPage, setCurrentPage] = useState(1);
  const activeSectionRef = useRef<HTMLDivElement>(null);
  const shouldScrollRef = useRef(false);

  const tabIds = useMemo(
    () => sections.map(({ section }) => stegaClean(section.articleType)).filter(Boolean),
    [sections],
  );

  // Use hash action to set active tab when ID matches
  useHashAction(
    tabIds,
    (matchedHash) => {
      const sectionByType = sections.find(({ section }) => section.articleType === matchedHash);
      if (sectionByType) {
        shouldScrollRef.current = true;
        setActiveTab(sectionByType.section._key);
        setCurrentPage(1);
      } else {
        shouldScrollRef.current = false;
        setActiveTab(sections[0]?.section._key || "");
        setCurrentPage(1);
      }
    },
    { enabled: tabIds.length > 0 },
  );

  // Use hash pagination to set active tab and page when ID matches
  useHashPagination(
    tabIds,
    ({ tab, page }) => {
      const sectionByType = sections.find(({ section }) => section.articleType === tab);
      if (sectionByType) {
        shouldScrollRef.current = true;
        setActiveTab(sectionByType.section._key);
        setCurrentPage(page);
      } else {
        shouldScrollRef.current = false;
        setActiveTab(sections[0]?.section._key || "");
        setCurrentPage(1);
      }
    },
    { enabled: tabIds.length > 0 },
  );

  useEffect(() => {
    if (shouldScrollRef.current && activeSectionRef.current) {
      activeSectionRef.current.scrollIntoView({ behavior: "smooth" });
      shouldScrollRef.current = false;
    }
  }, [activeTab, currentPage]);

  const handleTabClick = (section: ContentHubSection) => {
    shouldScrollRef.current = true;
    setActiveTab(section._key);
    setCurrentPage(1);
    window.history.replaceState(null, "", `#${section.articleType}`);
  };

  const activeSection = sections.find((s) => s.section._key === activeTab) || sections[0];

  if (!activeSection) return null;

  const totalPages = Math.ceil(activeSection.articles.length / ARTICLES_PER_PAGE);
  const startIndex = (currentPage - 1) * ARTICLES_PER_PAGE;
  const endIndex = startIndex + ARTICLES_PER_PAGE;
  const paginatedArticles = activeSection.articles.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Update URL hash to include page number
    const currentSection = sections.find((s) => s.section._key === activeTab);
    if (currentSection) {
      const hashUrl =
        page === 1
          ? `#${currentSection.section.articleType}`
          : `#${currentSection.section.articleType}&page=${page}`;
      window.history.replaceState(null, "", hashUrl);
    }
  };

  const handleScroll = () => {
    // Use double requestAnimationFrame to ensure the element is rendered and layout is updated
    // before calling scrollIntoView. This fixes cross-browser issues where Safari/Firefox
    // ignore smooth scrolling if called immediately after state update or history change.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (activeSectionRef.current) {
          activeSectionRef.current.scrollIntoView({ behavior: "smooth" });
        }
      });
    });
  };

  return (
    <>
      {/* Tabs */}
      <section className="gap-gap-element-sm inline-flex flex-col items-center justify-center border-b-2 border-white/20 pt-28 pb-12">
        <div className="gap-gap-element-xl inline-flex h-12 items-start justify-start self-stretch overflow-x-auto overflow-y-hidden px-8">
          {sections.map(({ section }) => {
            const isActive = section._key === activeTab;
            return (
              <Button
                key={section._key}
                variant="link"
                onClick={() => handleTabClick(section)}
                className={cn(
                  "typography-heading-4! h-auto justify-start p-0",
                  isActive ? "text-foreground" : "text-neutral",
                )}
              >
                {section.title}
              </Button>
            );
          })}
        </div>
      </section>

      {/* Active Section Content */}
      <section ref={activeSectionRef} className="py-padding-section-md">
        {/* Article Count */}
        <div className="mb-padding-element-lg">
          <h3 className="typography-heading-4 text-foreground">
            {activeSection.articles.length} {activeSection.section.title} Article
            {activeSection.articles.length !== 1 ? "s" : ""}
          </h3>
        </div>

        {/* Article Grid */}
        {activeSection.articles.length > 0 ? (
          <>
            <div className="gap-gap-element-lg mb-padding-section-md grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              {paginatedArticles.map((article) => (
                <ArticleCard key={article._id} article={article} />
              ))}
            </div>

            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              onScroll={handleScroll}
            />
          </>
        ) : (
          <div className="py-padding-section-md text-center">
            <p className="typography-text-regular-normal text-neutral">
              No articles found in this section.
            </p>
          </div>
        )}
      </section>
    </>
  );
}
